import type { ReportSubmissionStat } from "@/types";
import { Card, CardContent, CardHeader } from "@/components/ui/card";
import { Progress } from "@/components/ui/progress";
import { Skeleton } from "@/components/ui/skeleton";
import { formatYmd } from "./report-status-meta";

/**
 * T-44b — weekly-report submission for the last COMPLETED report week (Mon..Sun, Central time).
 * Same expected set as the Weekly Compliance card. "N of M submitted" is plain text; the progress
 * bar is labelled for assistive tech.
 */
export interface ReportSubmissionCardProps {
  data: ReportSubmissionStat | undefined;
  isLoading?: boolean;
  className?: string;
}

export function ReportSubmissionCard({
  data,
  isLoading = false,
  className,
}: ReportSubmissionCardProps): JSX.Element {
  const pct =
    data && data.expected > 0
      ? Math.round((data.submitted / data.expected) * 100)
      : 0;
  const weekLabel = data
    ? `${formatYmd(data.week_start)} – ${formatYmd(data.week_end)}`
    : "";

  return (
    <Card className={className}>
      <CardHeader>
        <h3 className="text-base font-semibold text-text-primary">
          Last Week&apos;s Reports
        </h3>
        <p className="text-sm text-text-secondary">
          {data ? `Week of ${weekLabel}` : "Last completed report week"}
        </p>
      </CardHeader>
      <CardContent>
        {isLoading ? (
          <div data-testid="submission-loading">
            <Skeleton className="h-28 w-full rounded-lg" />
          </div>
        ) : !data ? (
          <p className="py-8 text-center text-sm text-text-secondary">
            Submission data unavailable.
          </p>
        ) : data.expected === 0 ? (
          <p className="py-8 text-center text-sm text-text-secondary">
            No projects were expected to report last week.
          </p>
        ) : (
          <div className="space-y-4">
            <p className="text-text-primary">
              <span className="text-3xl font-bold tracking-tight">
                {data.submitted}
              </span>
              <span className="text-sm text-text-secondary">
                {" "}
                of {data.expected} submitted
              </span>
            </p>
            <Progress
              value={pct}
              className="h-2"
              aria-label={`${data.submitted} of ${data.expected} reports submitted`}
            />
            <dl className="grid grid-cols-2 gap-x-4 gap-y-1 text-sm">
              <dt className="text-text-secondary">Missing</dt>
              <dd className="text-right font-semibold text-text-primary">
                {data.missing}
              </dd>
              <dt className="text-text-secondary">Awaiting review</dt>
              <dd className="text-right font-semibold text-text-primary">
                {data.pending_review}
              </dd>
              <dt className="text-text-secondary">Changes requested</dt>
              <dd className="text-right font-semibold text-text-primary">
                {data.changes_requested}
              </dd>
              <dt className="text-text-secondary">Approved</dt>
              <dd className="text-right font-semibold text-text-primary">
                {data.approved}
              </dd>
            </dl>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
